// frontend/src/tour/tourEngine.ts
// Imperatives Overlay fuer die Tindra-gefuehrte In-App-Tour.
//
// Inspiriert von frontend/src/pages/onboarding/characterTour.ts (DOM-Coachmark-
// Engine), aber INVERTiert: diese Engine TREIBT die Navigation selbst statt
// auf popstate/MutationObserver abzubrechen. Die Tour ist eine State-Machine
// aus Schritten, die jeweils zu einer Ziel-Route navigieren und dort einen
// Anchor im DOM highlighten.
//
// Schritt-Varianten:
//   - Standard: Weiter / Zurueck / Ueberspringen. Bubble positioniert am
//     Ziel; animierter Finger-Zeiger daneben (sofern anchor vorhanden).
//   - Interactive (`interactive: true`): KEIN Weiter-Button. Schritt geht
//     NUR weiter, wenn der User das hervorgehobene Ziel selbst antippt. Die
//     Eingabesperre laeuft ueber einen Document-Capture-Listener: Klicks
//     ausserhalb von Ziel und Tip-Bubble werden geblockt (No-Op). Der
//     Backdrop im DOM ist nur noch visuell (pointer-events:none), damit das
//     Ziel auf iOS-WKWebView nicht von einem body-level-Overlay ueberdeckt
//     wird (das selbst z-index:10000 !important auf der Highlight-Klasse
//     dort nicht zuverlaessig aufloest). Fluchtweg: dezenter
//     "Tour ueberspringen"-Link in der Bubble + Esc. Die echte Element-
//     Aktion (z.B. Navigation) laeuft danach ganz normal weiter — wir
//     lassen den Klick durch.
//
// Lebenszyklus:
//   startTour({ steps, t, navigate, onComplete, onSkip }) -> Engine laeuft
//   active.stop()                                        -> sauber abbrechen
//   je Schritt: navigate(step.route) -> poll(anchor) -> Bubble + Zeiger positionieren
//   Klick ausserhalb (Standard-Schritt) / Esc / "Ueberspringen" = Skip
//     (ausser im interactive-Schritt, wo Klicks ausserhalb geblockt werden
//     und nur Esc + Skip-Link als Notausgang gelten).
//
// Cleanup: nach Abschluss/Skip entfernt die Engine Overlay + Listener wieder.
// Es bleibt KEIN Vollbild-Sperr-Backdrop im DOM haengen.

export interface TourStep {
  /** Ziel-Route, zu der vor dem Highlight navigiert wird. */
  route: string;
  /** Optionaler data-tour-anchor — wenn fehlt/Timeout, Bubble zentriert. */
  anchor?: string;
  /** Fallback-Anchor, falls `anchor` nicht im DOM auffindbar ist. Wird vor
   *  dem Zentrieren probiert (fuer adaptive Schritte, z.B. "erste Kampagne"
   *  vs. "Neues Abenteuer"). */
  fallbackAnchor?: string;
  /** i18n-Key fuer den Bubble-Titel. */
  titleKey: string;
  /** i18n-Key fuer den Bubble-Body. */
  bodyKey: string;
  /** Wenn true: kein Weiter-Button. Schritt geht nur durch Klick auf das
   *  hervorgehobene Ziel weiter. Backdrop-Klicks sind No-Op. */
  interactive?: boolean;
  /** Animierten Finger-Zeiger anzeigen. Default: true, sobald ein Anchor
   *  aufgeloest wurde; auf zentrierten Intro-Schritten ohne Ziel entfaellt er. */
  pointer?: boolean;
}

export interface StartTourOptions {
  steps: TourStep[];
  t: (key: string) => string;
  /** React-Router navigate — von aussen uebergeben, damit die Engine
   *  keinen Router-Hook braucht und auch in Tests funktioniert. */
  navigate: (path: string) => void;
  /** Optional: URL fuer den Tindra-Avatar im Bubble-Header. */
  avatarUrl?: string;
  /** Schritt-Index, bei dem die Tour startet (Default 0). Ermoeglicht das
   *  Wiederaufnehmen nach einer Pause an genau der Stelle, an der pausiert
   *  wurde (CRIT 3). */
  startIndex?: number;
  /** Wird nach dem LETZTEN Schritt aufgerufen. Auch wenn der letzte Schritt
   *  interactive ist, wird onComplete erst nach dem Ziel-Klick gerufen. */
  onComplete: () => void;
  /** Wird bei einem EXPLIZITEN Skip aufgerufen — der "Ueberspringen"-Button
   *  bzw. -Link in der Bubble. Nur das zaehlt als endgueltiges Beenden der
   *  Tour (done-Flag). */
  onSkip?: () => void;
  /** Wird bei einer PAUSE aufgerufen (Klick neben die Tour / Esc). Anders als
   *  onSkip beendet eine Pause die Tour NICHT endgueltig — der Aufrufer merkt
   *  sich den Schritt-Index und bietet die Wiederaufnahme an (CRIT 3). Fehlt
   *  der Handler, verhaelt sich eine Pause wie ein stiller Abbruch OHNE
   *  done-Flag. */
  onPause?: (index: number) => void;
}

export interface ActiveTour {
  /** Stoppt die Engine sauber (idempotent). */
  stop: () => void;
}

const OVERLAY_ID = 'lorath-tour-overlay';
const ANCHOR_POLL_MS = 120;
const ANCHOR_POLL_TIMEOUT_MS = 6000;
const HIGHLIGHT_CLASS = 'lorath-tour-highlight';
const ROUTE_POLL_MS = 50;
const ROUTE_POLL_TIMEOUT_MS = 6000;
const INTERACTIVE_LOCK_CLASS = 'lorath-tour-locked';

// Sprite-IDs sind ohne "icon-"-Prefix benannt; <use href> braucht das Prefix.
const ICON_PREV = 'arrow-left';
const ICON_NEXT = 'arrow-right';
const ICON_SKIP = 'close';
const ICON_FINISH = 'check';
// Hand-/Finger-Zeiger (siehe frontend/src/assets/icons-sprite.svg).
const ICON_POINTER = 'hand-point';

let activeCleanup: (() => void) | null = null;

// ─── Wiederaufnahme-Pill (W2-B/N-05) ───────────────────────────────────────
// Die Pill „Tour pausiert" lag fixiert (z-index 9998) genau auf der Eingabe-
// zeile der Chats bzw. ueber der letzten Nachricht. In Chat-Routen wird sie
// deshalb gar nicht gerendert; ueberall sonst klappt sie nach
// TOUR_PILL_COLLAPSE_MS auf das Tindra-Symbol zusammen (Tipp klappt sie auf).
export const TOUR_PILL_COLLAPSE_MS = 10_000;

const TOUR_PILL_HIDDEN_ROUTES: readonly RegExp[] = [
  /^\/chat\//,
  /^\/character-chat\//,
  /^\/group-chat\//,
  /^\/table-round-chat\//,
  /^\/table-round-session\//,
];

/** true = auf dieser Route keine Wiederaufnahme-Pill (laufender Chat). */
export function isTourPillHiddenRoute(pathname: string): boolean {
  return TOUR_PILL_HIDDEN_ROUTES.some((pattern) => pattern.test(pathname));
}

/**
 * Injiziert die Tour-Styles idempotent. Exportiert, damit auch das
 * Wiederaufnahme-Pill (TourProvider) die Klassen nutzen kann, ohne dass gerade
 * eine Tour laeuft (nach einer Pause ist die Overlay-Instanz weg, das
 * <style>-Element bleibt aber im <head>).
 */
export function ensureTourStyles(): void {
  injectStylesOnce();
}

function injectStylesOnce(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('lorath-tour-styles')) return;
  const style = document.createElement('style');
  style.id = 'lorath-tour-styles';
  // Bewusst inline, damit die Tour ohne CSS-Modul-Abhaengigkeit ueberall greift.
  // KEINE Emoji, alles ueber das SVG-Sprite (siehe <use href="#icon-...">).
  style.textContent = `
    #${OVERLAY_ID} { position: fixed; inset: 0; z-index: 9999; pointer-events: none; }
    /* Backdrop ist nur noch visuell (Abdunkelung fuer zentrierte Intro-Schritte
       OHNE aufgeloestes Ziel). Bei Schritten MIT Highlight erledigt der Spread
       der Highlight-Box-Shadow-Klasse (0 0 0 9999px rgba(0,0,0,0.55)) die
       Abdunkelung — und der laesst das Ziel zuverlaessig klickbar, was auf
       iOS-WKWebView mit einem blockierenden body-level-Overlay sonst nicht
       funktioniert hat (Hit-Testing-Blocker ueber tief im App-DOM liegenden
       Zielen). Eingabesperre laeuft jetzt ueber einen Document-Capture-
       Listener (siehe onCaptureInput in startTour). */
    #${OVERLAY_ID} .lorath-tour-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,0.55); pointer-events: none; }
    /* KEIN "position: relative" hier! Die Regel hat gleiche Spezifitaet wie die
       Modul-Klassen, das Tour-<style> steht aber spaeter im <head> und gewinnt
       deshalb — und riss damit jedes fixierte Ziel aus seiner Fixierung. Auf
       375x812 fiel die BottomNav in Schritt 2 ("Die Bereiche unten") von ihrer
       Position auf y=792..856, also zu zwei Dritteln unter den sichtbaren Rand:
       die Tour erklaerte eine Leiste, die der Nutzer nicht mehr sah.
       z-index braucht nur EINEN Positionierungskontext — ob fixed, absolute,
       sticky oder relative ist egal. Deshalb setzt startTour() "relative" jetzt
       gezielt inline und NUR dann, wenn das Ziel wirklich "static" ist. */
    .${HIGHLIGHT_CLASS} {
      z-index: 10000 !important;
      box-shadow: 0 0 0 3px var(--ff-gold), 0 0 0 9999px rgba(0,0,0,0.55);
      border-radius: 10px;
      animation: lorath-tour-pulse 1.6s ease-in-out infinite;
    }
    @keyframes lorath-tour-pulse {
      0%   { box-shadow: 0 0 0 3px var(--ff-gold), 0 0 0 9999px rgba(0,0,0,0.55); }
      50%  { box-shadow: 0 0 0 6px var(--ff-gold), 0 0 0 9999px rgba(0,0,0,0.55); }
      100% { box-shadow: 0 0 0 3px var(--ff-gold), 0 0 0 9999px rgba(0,0,0,0.55); }
    }
    #${OVERLAY_ID} .lorath-tour-pointer {
      position: absolute;
      inline-size: 48px;
      block-size: 48px;
      color: var(--ff-gold);
      pointer-events: none;
      filter: drop-shadow(0 4px 10px rgba(0,0,0,0.55));
      z-index: 10001;
      transform-origin: 50% 50%;
      animation: lorath-tour-bob 0.9s ease-in-out infinite;
    }
    @keyframes lorath-tour-bob {
      0%   { transform: translate(-50%, 0) rotate(-8deg); }
      50%  { transform: translate(-50%, -10px) rotate(8deg); }
      100% { transform: translate(-50%, 0) rotate(-8deg); }
    }
    #${OVERLAY_ID} .lorath-tour-pointer.above {
      animation: lorath-tour-bob-above 0.9s ease-in-out infinite;
    }
    @keyframes lorath-tour-bob-above {
      0%   { transform: translate(-50%, 0) rotate(180deg) scaleX(-1); }
      50%  { transform: translate(-50%, 10px) rotate(180deg) scaleX(-1); }
      100% { transform: translate(-50%, 0) rotate(180deg) scaleX(-1); }
    }
    #${OVERLAY_ID} .lorath-tour-tip { position: absolute; width: min(340px, calc(100dvw - 32px)); background: var(--ff-bg-card); color: var(--ff-text-primary); border: 1px solid var(--ff-gold); border-radius: 14px; padding: 14px 16px; box-shadow: 0 12px 40px rgba(0,0,0,0.55); pointer-events: auto; font-size: 15px; line-height: 1.45; }
    #${OVERLAY_ID} .lorath-tour-tip.centered { left: 50% !important; top: 50% !important; transform: translate(-50%, -50%); }
    #${OVERLAY_ID} .lorath-tour-header { display: flex; align-items: center; gap: 10px; margin-block-end: 8px; }
    #${OVERLAY_ID} .lorath-tour-avatar { inline-size: 38px; block-size: 38px; border-radius: 999px; object-fit: cover; border: 1px solid rgba(var(--ff-gold-rgb), 0.5); background: var(--ff-bg-secondary); flex: 0 0 auto; }
    #${OVERLAY_ID} .lorath-tour-title { margin: 0; font-size: 1rem; line-height: 1.3; color: var(--ff-gold); font-family: var(--ff-font-heading, inherit); }
    #${OVERLAY_ID} .lorath-tour-body { margin: 0; color: var(--ff-text-primary); }
    #${OVERLAY_ID} .lorath-tour-actions { display: flex; gap: 8px; margin-block-start: 12px; justify-content: space-between; align-items: center; flex-wrap: wrap; }
    #${OVERLAY_ID} .lorath-tour-btn { display: inline-flex; align-items: center; gap: 6px; border: 1px solid rgba(var(--ff-gold-rgb), 0.4); background: transparent; color: var(--ff-text-primary); border-radius: 8px; padding: 7px 12px; font: inherit; cursor: pointer; }
    #${OVERLAY_ID} .lorath-tour-btn:hover { border-color: var(--ff-gold); }
    #${OVERLAY_ID} .lorath-tour-btn:focus-visible { outline: 2px solid var(--ff-gold); outline-offset: 2px; }
    #${OVERLAY_ID} .lorath-tour-btn.primary { background: var(--ff-gold); color: var(--ff-on-gold); border-color: var(--ff-gold); font-weight: 600; }
    #${OVERLAY_ID} .lorath-tour-btn svg { inline-size: 16px; block-size: 16px; fill: currentColor; }
    #${OVERLAY_ID} .lorath-tour-progress { font-size: 0.8rem; opacity: 0.75; }
    #${OVERLAY_ID} .lorath-tour-skip-link {
      background: none;
      border: 0;
      padding: 6px 4px;
      color: var(--ff-text-muted);
      font: inherit;
      font-size: 0.82rem;
      cursor: pointer;
      pointer-events: auto;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    #${OVERLAY_ID} .lorath-tour-skip-link:hover { color: var(--ff-gold); }
    /* Im interactive-Lock: Scroll unterbinden, damit das Ziel nicht weg-
       gescrollt werden kann. Die Eingabesperre laeuft NICHT mehr ueber den
       Backdrop (der ist pointer-events:none), sondern ueber den Document-
       Capture-Listener (siehe onCaptureInput in startTour). */
    body.${INTERACTIVE_LOCK_CLASS} { overflow: hidden; }
    /* Wiederaufnahme-Pill (CRIT 3): erscheint, nachdem eine Tour PAUSIERT
       wurde (Klick daneben / Esc). Bietet die Fortsetzung an, statt die Tour
       lautlos endgueltig zu beenden. Bewusst dezent unten zentriert, ueber
       der BottomNav (die endet bei ~54px + Safe-Area). */
    .lorath-tour-resume {
      position: fixed;
      left: 50%;
      transform: translateX(-50%);
      bottom: calc(76px + env(safe-area-inset-bottom, 0px));
      z-index: 9998;
      display: flex;
      align-items: center;
      gap: 10px;
      max-inline-size: min(360px, calc(100dvw - 24px));
      background: var(--ff-bg-card);
      color: var(--ff-text-primary);
      border: 1px solid var(--ff-gold);
      border-radius: 999px;
      padding: 8px 10px 8px 14px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.45);
    }
    @media (min-width: 600px) { .lorath-tour-resume { bottom: calc(24px + env(safe-area-inset-bottom, 0px)); } }
    .lorath-tour-resume-avatar { inline-size: 30px; block-size: 30px; border-radius: 999px; object-fit: cover; flex: 0 0 auto; border: 1px solid rgba(var(--ff-gold-rgb), 0.5); }
    .lorath-tour-resume-text { font-size: 0.9rem; line-height: 1.3; }
    .lorath-tour-resume-actions { display: inline-flex; gap: 6px; margin-inline-start: auto; }
    .lorath-tour-resume-btn { display: inline-flex; align-items: center; border: 1px solid rgba(var(--ff-gold-rgb), 0.4); background: transparent; color: var(--ff-text-primary); border-radius: 999px; padding: 6px 12px; font: inherit; font-size: 0.85rem; cursor: pointer; }
    .lorath-tour-resume-btn.primary { background: var(--ff-gold); color: var(--ff-on-gold); border-color: var(--ff-gold); font-weight: 600; }
    .lorath-tour-resume-btn:hover { border-color: var(--ff-gold); }
    .lorath-tour-resume-btn:focus-visible { outline: 2px solid var(--ff-gold); outline-offset: 2px; }
    /* W2-B/N-05: eingeklappt nach 10 s — nur noch das Tindra-Symbol am Rand,
       damit die Pill nicht dauerhaft Inhalt oder Leiste verdeckt. Ein Tipp
       klappt sie wieder auf. inset-inline-end folgt der Schreibrichtung. */
    .lorath-tour-resume.is-collapsed {
      left: auto;
      inset-inline-end: calc(16px + env(safe-area-inset-right, 0px));
      transform: none;
      gap: 0;
      padding: 4px;
      cursor: pointer;
      font: inherit;
    }
    .lorath-tour-resume.is-collapsed .lorath-tour-resume-avatar { inline-size: 36px; block-size: 36px; }
    .lorath-tour-resume.is-collapsed:focus-visible { outline: 2px solid var(--ff-gold); outline-offset: 2px; }
  `;
  document.head.appendChild(style);
}

function makeIconButton(iconName: string, label: string, primary: boolean, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `lorath-tour-btn${primary ? ' primary' : ''}`;
  // SVG aus dem Sprite (kein Emoji — CLAUDE.md §17).
  const svgNs = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNs, 'svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const use = document.createElementNS(svgNs, 'use');
  use.setAttribute('href', `#icon-${iconName}`);
  svg.appendChild(use);
  const span = document.createElement('span');
  span.textContent = label;
  // Primary: Text links, Icon rechts (Vorwaerts-Bewegung). Sonst: Icon links, Text rechts.
  if (primary) { btn.appendChild(span); btn.appendChild(svg); } else { btn.appendChild(svg); btn.appendChild(span); }
  btn.addEventListener('click', onClick);
  return btn;
}

function makeSkipLink(label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'lorath-tour-skip-link';
  btn.textContent = label;
  btn.addEventListener('click', onClick);
  return btn;
}

function makePointer(): SVGSVGElement {
  const svgNs = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNs, 'svg');
  svg.setAttribute('class', 'lorath-tour-pointer');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const use = document.createElementNS(svgNs, 'use');
  use.setAttribute('href', `#icon-${ICON_POINTER}`);
  svg.appendChild(use);
  return svg;
}

function findAnchor(value: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-tour-anchor="${value}"]`);
}

/**
 * CRIT 5: Ein Anchor kann im DOM vorhanden, aber unsichtbar sein — die
 * BottomNav (`bottom-nav`) ist ab 600px (E0/Q9) `display:none`, existiert aber weiter
 * im DOM. Wuerden wir sie highlighten, saesse der Spotlight-Ring um eine 0x0-Box
 * und der Zeiger erklaerte eine Leiste, die der Nutzer nicht sieht. Deshalb
 * zaehlt ein Anchor nur als aufloesbar, wenn er tatsaechlich gerendert ist.
 */
function isAnchorVisible(el: HTMLElement): boolean {
  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return true;
  const style = window.getComputedStyle(el);
  // `display:none` deckt den konkreten Fall ab: die BottomNav (`bottom-nav`) ist
  // ab 600px (E0/Q9) per Media-Query display:none — im echten Browser spiegelt
  // getComputedStyle().display das wider. Bewusst KEIN getBoundingClientRect-
  // Groessencheck: jsdom liefert fuer ALLES 0x0 (keine Layout-Engine), das wuerde
  // in Tests jeden Anchor faelschlich als unsichtbar werten. display/visibility/
  // opacity reichen fuer den Realfall und sind jsdom-tauglich (Inline-Styles).
  if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
  if (parseFloat(style.opacity || '1') === 0) return false;
  return true;
}

function positionTip(tip: HTMLElement, target: HTMLElement): void {
  const rect = target.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  // Standard: unterhalb des Ziels; faellt nach oben, wenn unten kein Platz ist.
  let top = rect.bottom + 12;
  if (top + tipRect.height > window.innerHeight - 12) {
    top = Math.max(12, rect.top - tipRect.height - 12);
  }
  let left = rect.left;
  if (left + tipRect.width > window.innerWidth - 12) {
    left = Math.max(12, window.innerWidth - tipRect.width - 12);
  }
  tip.style.top = `${Math.max(12, top)}px`;
  tip.style.left = `${left}px`;
  tip.classList.remove('centered');
}

function positionPointer(pointer: SVGSVGElement, target: HTMLElement): void {
  const rect = target.getBoundingClientRect();
  const pointerWidth = 48;
  const pointerHeight = 48;
  const belowRoom = window.innerHeight - rect.bottom;
  const aboveRoom = rect.top;
  // Standard: unterhalb. Wenn zu wenig Platz (BottomNav, kleiner Viewport),
  // dann oberhalb mit gedrehtem Icon.
  if (belowRoom >= pointerHeight + 16) {
    pointer.classList.remove('above');
    pointer.style.top = `${rect.bottom + 12}px`;
  } else if (aboveRoom >= pointerHeight + 16) {
    pointer.classList.add('above');
    pointer.style.top = `${rect.top - 12}px`;
  } else {
    // Letzter Ausweg: rechts neben dem Ziel.
    pointer.classList.remove('above');
    pointer.style.top = `${Math.max(8, rect.top + rect.height / 2 - pointerHeight / 2)}px`;
  }
  let left = rect.left + rect.width / 2;
  if (left - pointerWidth / 2 < 8) left = pointerWidth / 2 + 8;
  if (left + pointerWidth / 2 > window.innerWidth - 8) left = window.innerWidth - pointerWidth / 2 - 8;
  pointer.style.left = `${left}px`;
}

function centerTip(tip: HTMLElement): void {
  // Wenn kein Anchor sichtbar ist, zeigen wir die Bubble zentriert
  // (step.route wurde zwar navigiert, aber das Element existiert nicht im DOM).
  tip.classList.add('centered');
  tip.style.top = '';
  tip.style.left = '';
}

interface BuildTipHandlers {
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
}

function buildTip(step: TourStep, index: number, total: number, opts: StartTourOptions, handlers: BuildTipHandlers): HTMLElement {
  const tip = document.createElement('div');
  tip.className = 'lorath-tour-tip';
  tip.setAttribute('role', 'dialog');
  tip.setAttribute('aria-label', opts.t(step.titleKey));

  const header = document.createElement('div');
  header.className = 'lorath-tour-header';

  if (opts.avatarUrl) {
    const img = document.createElement('img');
    img.className = 'lorath-tour-avatar';
    img.src = opts.avatarUrl;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.onerror = () => { img.style.display = 'none'; };
    header.appendChild(img);
  }

  const title = document.createElement('h2');
  title.className = 'lorath-tour-title';
  title.textContent = opts.t(step.titleKey);
  header.appendChild(title);
  tip.appendChild(header);

  const body = document.createElement('p');
  body.className = 'lorath-tour-body';
  body.textContent = opts.t(step.bodyKey);
  tip.appendChild(body);

  const actions = document.createElement('div');
  actions.className = 'lorath-tour-actions';

  if (step.interactive) {
    // Im interactive-Schritt gibt es bewusst KEINEN Weiter-Button — der User
    // muss das Ziel selbst antippen. Als Notausgang bleibt der dezente
    // Skip-Link + Esc-Taste.
    actions.appendChild(makeSkipLink(opts.t('tour.buttons.skip'), handlers.onSkip));
  } else {
    // Linke Seite: Skip (immer sichtbar).
    actions.appendChild(makeIconButton(ICON_SKIP, opts.t('tour.buttons.skip'), false, handlers.onSkip));
    // Zurueck nur ab Schritt 2.
    if (index > 0) {
      actions.appendChild(makeIconButton(ICON_PREV, opts.t('tour.buttons.back'), false, handlers.onPrev));
    }

    // Fortschritt (rechts, dezent).
    const progress = document.createElement('span');
    progress.className = 'lorath-tour-progress';
    progress.textContent = opts
      .t('tour.progress')
      .replace('{current}', String(index + 1))
      .replace('{total}', String(total));
    actions.appendChild(progress);

    // Primary-Button: "Weiter" bzw. "Fertig" im letzten Schritt.
    const isLast = index >= total - 1;
    const primaryLabel = isLast ? opts.t('tour.buttons.finish') : opts.t('tour.buttons.next');
    const primaryIcon = isLast ? ICON_FINISH : ICON_NEXT;
    actions.appendChild(makeIconButton(primaryIcon, primaryLabel, true, handlers.onNext));
  }

  tip.appendChild(actions);
  return tip;
}

export function startTour(opts: StartTourOptions): ActiveTour {
  if (typeof document === 'undefined') {
    // SSR / kein DOM → onComplete sofort feuern, damit der Aufrufer nicht haengt.
    try { opts.onComplete(); } catch { /* ignore */ }
    return { stop: () => { /* no-op */ } };
  }

  // Bereits laufende Tour beenden (kein Doppel-Overlay).
  if (activeCleanup) { try { activeCleanup(); } catch { /* ignore */ } }

  injectStylesOnce();

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  const backdrop = document.createElement('div');
  backdrop.className = 'lorath-tour-backdrop';
  overlay.appendChild(backdrop);
  document.body.appendChild(overlay);

  const total = opts.steps.length;
  // startIndex erlaubt die Wiederaufnahme an der Pausenstelle (CRIT 3). Auf den
  // gueltigen Bereich klemmen, damit ein veralteter Index nicht ins Leere zeigt.
  let stepIndex = Math.min(Math.max(0, opts.startIndex ?? 0), Math.max(0, total - 1));
  let currentHighlight: HTMLElement | null = null;
  let tip: HTMLElement | null = null;
  let pointer: SVGSVGElement | null = null;
  let interactiveClickHandler: ((event: MouseEvent) => void) | null = null;
  let pollTimer: number | null = null;
  let routeTimer: number | null = null;
  let finished = false;

  // Merker: haben WIR dem Ziel "position: relative" verpasst? Nur dann darf es
  // beim Aufraeumen wieder weg — ein Ziel mit eigenem Inline-Style bleibt heil.
  let highlightPositionPatched = false;

  const removeHighlight = () => {
    if (currentHighlight) {
      currentHighlight.classList.remove(HIGHLIGHT_CLASS);
      if (highlightPositionPatched) {
        currentHighlight.style.removeProperty('position');
        highlightPositionPatched = false;
      }
      currentHighlight = null;
    }
  };

  const removePointer = () => {
    if (pointer?.parentElement) {
      pointer.parentElement.removeChild(pointer);
    }
    pointer = null;
  };

  const removeInteractiveListener = () => {
    if (interactiveClickHandler && currentHighlight) {
      currentHighlight.removeEventListener('click', interactiveClickHandler);
    }
    interactiveClickHandler = null;
  };

  const clearPoll = () => {
    if (pollTimer != null) {
      window.clearTimeout(pollTimer);
      pollTimer = null;
    }
  };

  const clearRoutePoll = () => {
    if (routeTimer != null) {
      window.clearTimeout(routeTimer);
      routeTimer = null;
    }
  };

  const setInteractiveLock = (locked: boolean) => {
    if (typeof document === 'undefined') return;
    try {
      if (locked) document.body.classList.add(INTERACTIVE_LOCK_CLASS);
      else document.body.classList.remove(INTERACTIVE_LOCK_CLASS);
    } catch { /* ignore */ }
  };

  function onReposition() {
    if (!tip) return;
    if (currentHighlight) {
      positionTip(tip, currentHighlight);
      if (pointer) positionPointer(pointer, currentHighlight);
    } else {
      centerTip(tip);
    }
  }

  const cleanup = () => {
    clearPoll();
    clearRoutePoll();
    removeHighlight();
    removePointer();
    removeInteractiveListener();
    setInteractiveLock(false);
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('keydown', onKeyDown);
    }
    if (typeof document !== 'undefined') {
      // Document-Capture-Listener wieder entfernen — kein haengender Listener
      // nach Tour-Ende oder Navigation (sonst wuerde der naechste Klick im
      // Anschluss an die Tour ins Leere greifen oder andere Handler stoeren).
      document.removeEventListener('click', onCaptureInput, { capture: true });
      document.removeEventListener('pointerdown', onCaptureInput, { capture: true });
      document.removeEventListener('touchstart', onCaptureInput, { capture: true });
    }
    try { overlay.remove(); } catch { /* ignore */ }
    if (activeCleanup === cleanup) activeCleanup = null;
  };

  const finish = (kind: 'complete' | 'skip' | 'pause') => {
    if (finished) return;
    // Den aktuellen Schritt VOR dem cleanup festhalten — cleanup selbst laesst
    // stepIndex unangetastet, aber wir wollen den Wert defensiv sichern.
    const pausedAt = stepIndex;
    finished = true;
    cleanup();
    if (kind === 'complete') { try { opts.onComplete(); } catch { /* ignore */ } }
    else if (kind === 'pause') { try { opts.onPause?.(pausedAt); } catch { /* ignore */ } }
    else { try { opts.onSkip?.(); } catch { /* ignore */ } }
  };

  const skip = () => finish('skip');
  // Pause = "weg damit, aber nicht endgueltig". Wird bei Klick neben die Tour
  // und Esc ausgeloest (CRIT 3). Der explizite "Ueberspringen"-Button ruft
  // weiterhin skip() → nur DAS setzt das done-Flag.
  const pause = () => finish('pause');
  // Eingabesperre ueber einen Document-Capture-Listener (statt Backdrop-Klick):
  // Der Backdrop ist nur noch visuell (pointer-events:none). Damit iOS-WKWebView
  // das tief im App-DOM liegende Highlight-Ziel nicht durch ein body-level-
  // Overlay ueberdeckt (was selbst ein z-index:10000 !important auf der
  // Highlight-Klasse nicht zuverlaessig aufloest → Taps landen auf dem
  // Backdrop, User haengt fest), fangen wir Klicks/Pointer-Events oben ab
  // und entscheiden anhand des aktuellen Schritts:
  //   - Ziel oder Tip-Bubble          → durchlassen (echte Aktion laeuft)
  //   - Nicht-interactive + click     → skip() (wie bisher Backdrop-Klick)
  //   - Interactive                    → blocken (No-Op, Eingabe gesperrt)
  // currentHighlight / tip / stepIndex werden zur Laufzeit gelesen, damit
  // Schritt-Wechsel waehrend eines offenen Events korrekt reagieren — analog
  // zum vorherigen Backdrop-Handler, der opts.steps[stepIndex] las.
  function onCaptureInput(event: Event) {
    if (finished) return;
    const current = opts.steps[stepIndex];
    const target = event.target;
    if (!(target instanceof Node)) return;

    // Tip-Bubble (inkl. Buttons, Skip-Link) immer durchlassen.
    if (tip && (tip === target || tip.contains(target))) return;
    // Highlight-Ziel immer durchlassen — der bestehende Klick-Handler am
    // Ziel ruft go(1), und die echte Element-Aktion (React-onClick etc.)
    // laeuft danach normal weiter.
    if (currentHighlight && (currentHighlight === target || currentHighlight.contains(target))) return;

    if (current?.interactive) {
      // Eingabe gesperrt: Tip auf den abgedunkelten Bereich ist No-Op.
      event.preventDefault();
      event.stopPropagation();
    } else if (event.type === 'click') {
      // Standard-Schritt: Klick irgendwo ausserhalb PAUSIERT die Tour (CRIT 3),
      // statt sie lautlos endgueltig zu beenden. Der Aufrufer bietet die
      // Wiederaufnahme an; das done-Flag setzt erst ein echter Abschluss oder
      // der explizite "Ueberspringen"-Button.
      // pointerdown/touchstart lassen wir bewusst durch, damit der Klick am
      // Ende sicher feuert (Mobile-Browser unterdruecken Click-Synthese bei
      // preventDefault auf pointerdown).
      pause();
    }
  }
  document.addEventListener('click', onCaptureInput, { capture: true });
  document.addEventListener('pointerdown', onCaptureInput, { capture: true });
  // touchstart auf document ist per Spec passiv → ohne passive:false wuerde
  // iOS preventDefault() im interactive-Schritt ignorieren und durch das
  // Overlay hindurch scrollen.
  document.addEventListener('touchstart', onCaptureInput, { capture: true, passive: false });

  // Esc PAUSIERT die Tour (Accessibility + Notausgang fuer interactive-Schritte).
  // Wie der Aussenklick ist Esc bewusst kein endgueltiges Beenden — die
  // Wiederaufnahme wird angeboten (CRIT 3).
  function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      pause();
    }
  }
  window.addEventListener('keydown', onKeyDown);

  window.addEventListener('resize', onReposition);
  window.addEventListener('scroll', onReposition, true);

  const renderTip = (step: TourStep, index: number) => {
    if (tip) { tip.remove(); tip = null; }
    tip = buildTip(step, index, total, opts, {
      onPrev: () => go(-1),
      onNext: () => go(1),
      onSkip: skip,
    });
    overlay.appendChild(tip);
  };

  const gotoStep = (index: number) => {
    if (finished) return;
    if (index < 0 || index >= total) { finish('complete'); return; }
    stepIndex = index;
    showStep(stepIndex);
  };

  const go = (delta: number) => {
    if (finished) return;
    const next = stepIndex + delta;
    if (next < 0) return; // kein Back ueber Schritt 1 hinaus
    if (next >= total) { finish('complete'); return; }

    // Wenn die Ziel-Route eine andere ist, zuerst navigieren und warten, bis
    // die Seite gemountet hat (window.location.pathname). So verhindern wir
    // das "Anchor weg, Bubble haengt zentriert"-Glimmern waehrend der Navigation.
    const target = opts.steps[next];
    if (!target) { finish('complete'); return; }
    if (target.route !== window.location.pathname) {
      clearPoll();
      clearRoutePoll();
      removeHighlight();
      removePointer();
      removeInteractiveListener();
      setInteractiveLock(false);
      try { opts.navigate(target.route); } catch { /* ignore */ }
      // Bubble-Inhalt der ZWISCHENZEIT (alter Schritt) ausblenden, damit der
      // User nicht "Schritt 3 lesen" sieht, waehrend Schritt 4 schon laeuft.
      if (tip) { tip.remove(); tip = null; }
      const started = Date.now();
      const waitRoute = () => {
        if (finished) return;
        if (window.location.pathname === target.route) {
          routeTimer = null;
          gotoStep(next);
          return;
        }
        if (Date.now() - started > ROUTE_POLL_TIMEOUT_MS) {
          // Route-Wechsel hat nicht stattgefunden (z.B. RequireAuth-Blocker) →
          // trotzdem Schritt zeigen, zentriert.
          routeTimer = null;
          gotoStep(next);
          return;
        }
        routeTimer = window.setTimeout(waitRoute, ROUTE_POLL_MS);
      };
      routeTimer = window.setTimeout(waitRoute, ROUTE_POLL_MS);
      return;
    }
    gotoStep(next);
  };

  const resolveAnchor = (step: TourStep): HTMLElement | null => {
    if (step.anchor) {
      // CRIT 5: nur SICHTBARE Anchor zaehlen. Ist der Primaeranker vorhanden,
      // aber unsichtbar (BottomNav ab 600px), faellt die Aufloesung auf den
      // Ersatz-Anchor (z.B. AppHeader-Nav `header-nav`) durch.
      const primary = findAnchor(step.anchor);
      if (primary && isAnchorVisible(primary)) return primary;
      if (step.fallbackAnchor) {
        const fallback = findAnchor(step.fallbackAnchor);
        if (fallback && isAnchorVisible(fallback)) return fallback;
      }
    }
    return null;
  };

  const showStep = (index: number) => {
    if (finished) return;
    removeHighlight();
    clearPoll();
    removePointer();
    removeInteractiveListener();
    setInteractiveLock(false);

    if (index >= total) { finish('complete'); return; }
    const step = opts.steps[index];
    if (!step) { finish('complete'); return; }

    // Bubble SOFORT rendern, damit der User Feedback hat.
    renderTip(step, index);

    if (step.interactive) {
      setInteractiveLock(true);
    }

    if (!step.anchor) {
      // Kein Anchor → zentriert anzeigen, fertig.
      if (tip) centerTip(tip);
      return;
    }

    const started = Date.now();
    const poll = () => {
      if (finished) return;
      const target = resolveAnchor(step);
      if (target) {
        currentHighlight = target;
        target.classList.add(HIGHLIGHT_CLASS);
        // z-index wirkt nur in einem Positionierungskontext. Ein bereits
        // fixiertes/absolutes/sticky Ziel bringt den mit — nur ein "static"
        // Ziel braucht Nachhilfe. Inline statt in der Klasse, damit die Regel
        // nicht laenger blind jedes fixierte Ziel aus seiner Verankerung reisst.
        // Leerer String zaehlt wie "static": jsdom und einzelne aeltere Engines
        // liefern fuer ein nicht positioniertes Element nichts zurueck. Ohne
        // diesen Fall bliebe der z-index dort wirkungslos.
        const targetPosition = window.getComputedStyle(target).position;
        if (!targetPosition || targetPosition === 'static') {
          target.style.setProperty('position', 'relative');
          highlightPositionPatched = true;
        }
        try { target.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* ignore */ }
        if (tip) positionTip(tip, target);

        // Pointer nur, wenn der Step das nicht explizit abgeschaltet hat UND
        // wir gerade nicht in einem zentrierten Fall sind (resolveAnchor hat
        // einen DOM-Treffer, also unterstuetzen wir den Pointer).
        if (step.pointer !== false) {
          if (!pointer) {
            pointer = makePointer();
            overlay.appendChild(pointer);
          }
          positionPointer(pointer, target);
        }

        if (step.interactive) {
          // Im interactive-Schritt: Klick auf das Ziel = naechster Schritt /
          // Tour-Ende. Wir lassen den Klick durch (preventDefault NICHT), damit
          // die echte Aktion des Elements (Navigation, Form-Submit) danach
          // normal ablaufen kann. Listener loest sich nach dem ersten Klick
          // von selbst wieder.
          const handler = () => {
            if (finished) return;
            // Verzoegert auf naechsten Frame, damit der React-Synthetic-Event
            // der echten Aktion (z.B. onClick → navigate) noch in derselben
            // Task davor ablaufen kann. Reihenfolge: Native-Click (unser
            // Listener im Target-Phase) → React-Synthetic-Event im Bubble
            // (auf root delegiert) → unser Cleanup. react-Router-Dispatch
            // erfolgt erst nach unserem Cleanup → also: zuerst finishen,
            // dann laeuft die Navigation normal weiter.
            window.setTimeout(() => go(1), 0);
          };
          interactiveClickHandler = handler;
          target.addEventListener('click', handler);
        }
        return;
      }
      if (Date.now() - started > ANCHOR_POLL_TIMEOUT_MS) {
        // Anchor nicht gefunden → Bubble zentriert anzeigen, NICHT ueberspringen.
        if (tip) centerTip(tip);
        return;
      }
      pollTimer = window.setTimeout(poll, ANCHOR_POLL_MS);
    };
    // Mini-Delay, damit React nach navigate() den Anchor rendern kann.
    pollTimer = window.setTimeout(poll, ANCHOR_POLL_MS);
  };

  activeCleanup = cleanup;
  showStep(stepIndex);

  return {
    stop: () => finish('skip'),
  };
}
